/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * NOVA'S GEMS - MOTION CORE ENGINE v2.1 - WINTER '27 EDITION
 * Thread-safe, Theme Editor-compatible, BFCache-aware, 8-Layer Support
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * FEATURES:
 * ✅ Lenis smooth scrolling (physics-based inertia)
 * ✅ Shopify Theme Editor lifecycle (section load/unload)
 * ✅ BFCache-safe state reset
 * ✅ Module registry system (Renai FX modules)
 * ✅ Reduced motion compliance
 * ✅ 8-layer depth coordination
 * ✅ Variable font animation support
 * ✅ Haptic feedback coordination
 * ✅ Achievement system hooks
 * 
 * NEW IN V2.1 - WINTER '27:
 * ✨ 8-layer depth engine coordination
 * ✨ Variable font animation registry (font-variation-settings)
 * ✨ Haptic feedback API (navigator.vibrate)
 * ✨ Achievement unlock hooks (localStorage sync)
 * ✨ Gyroscope/DeviceOrientation coordination
 * ✨ Renaissance Journey module detection
 * 
 * DEPENDENCIES:
 * - Lenis (smooth scroll) - loaded via CDN in theme.liquid
 * - GSAP + ScrollTrigger (optional) - loaded via CDN
 * 
 * © 2024 Nova's Gems
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(() => {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════════
  // SINGLETON GUARD (prevent double-init in Theme Editor)
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (window.NovaMotion && window.NovaMotion.__initialized) {
    console.warn('⚠️ [NovaMotion] Already initialized - skipping');
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // GLOBAL STATE
  // ═══════════════════════════════════════════════════════════════════════════
  
  const state = {
    lenis: null,
    io: null,
    modules: new Map(),
    revealed: new WeakSet(),
    destroying: false,
    sectionCleanups: new Map(),
    moduleAbortControllers: new Map(),
    
    // WINTER '27: Extended state
    depthLayers: new Map(), // Track 8-layer systems
    variableFonts: new Map(), // Track variable font animations
    achievements: new Map(), // Track unlocked achievements
    hapticEnabled: false, // Haptic feedback availability
    orientationEnabled: false, // DeviceOrientation API availability
    renaissanceActive: false // Renaissance Journey detection
  };

  const prefersReduced = window.matchMedia && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const DEBUG = (() => {
    try { return localStorage.getItem('nova_debug') === 'true'; } 
    catch (e) { return false; }
  })();

  const log = (...args) => { 
    if (DEBUG) console.log('[NovaMotion v2.1]', ...args); 
  };

  const logCritical = (...args) => {
    // Silenced for production
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // WINTER '27: HAPTIC FEEDBACK SYSTEM
  // ═══════════════════════════════════════════════════════════════════════════

  const initHaptics = () => {
    if ('vibrate' in navigator) {
      state.hapticEnabled = true;
      log('✅ Haptic feedback available');
    } else {
      log('❌ Haptic feedback not supported');
    }
  };

  const triggerHaptic = (pattern = [10]) => {
    if (!state.hapticEnabled || prefersReduced) return false;
    
    try {
      navigator.vibrate(pattern);
      return true;
    } catch (e) {
      return false;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // WINTER '27: ACHIEVEMENT SYSTEM
  // ═══════════════════════════════════════════════════════════════════════════

  const loadAchievements = () => {
    try {
      const saved = localStorage.getItem('nova_achievements');
      if (saved) {
        const achievements = JSON.parse(saved);
        Object.entries(achievements).forEach(([key, data]) => {
          state.achievements.set(key, data);
        });
        log(`Loaded ${state.achievements.size} achievements`);
      }
    } catch (e) {
      log('Could not load achievements:', e);
    }
  };

  const unlockAchievement = (id, data = {}) => {
    if (state.achievements.has(id)) return false; // Already unlocked

    const achievement = {
      id,
      unlockedAt: Date.now(),
      ...data
    };

    state.achievements.set(id, achievement);

    // Save to localStorage
    try {
      const obj = {};
      state.achievements.forEach((val, key) => {
        obj[key] = val;
      });
      localStorage.setItem('nova_achievements', JSON.stringify(obj));
    } catch (e) {
      log('Could not save achievement:', e);
    }

    // Trigger haptic feedback
    triggerHaptic([10, 30, 10]);

    // Dispatch event for UI
    window.dispatchEvent(new CustomEvent('nova:achievement', { 
      detail: achievement 
    }));

    log(`🏆 Achievement unlocked: ${id}`);
    return true;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // WINTER '27: VARIABLE FONT ANIMATION REGISTRY
  // ═══════════════════════════════════════════════════════════════════════════

  const registerVariableFont = (element, config = {}) => {
    const {
      minWeight = 300,
      maxWeight = 700,
      axis = 'wght',
      scrollTrigger = null
    } = config;

    if (!element) return null;

    const fontId = `vf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    state.variableFonts.set(fontId, {
      element,
      minWeight,
      maxWeight,
      axis,
      scrollTrigger,
      currentWeight: minWeight
    });

    log(`Variable font registered: ${fontId}`);
    return fontId;
  };

  const updateVariableFont = (fontId, weight) => {
    const font = state.variableFonts.get(fontId);
    if (!font) return;

    const clampedWeight = Math.max(font.minWeight, Math.min(font.maxWeight, weight));
    font.currentWeight = clampedWeight;
    font.element.style.fontVariationSettings = `'${font.axis}' ${clampedWeight}`;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // WINTER '27: 8-LAYER DEPTH COORDINATION
  // ═══════════════════════════════════════════════════════════════════════════

  const register8LayerSystem = (systemId, config = {}) => {
    const {
      container = null,
      layers = [],
      parallaxEnabled = true,
      gyroscopeEnabled = true
    } = config;

    if (!container) {
      log('⚠️ Cannot register 8-layer system without container');
      return null;
    }

    state.depthLayers.set(systemId, {
      container,
      layers,
      parallaxEnabled,
      gyroscopeEnabled,
      active: true
    });

    logCritical(`✅ 8-layer system registered: ${systemId} (${layers.length} layers)`);
    return systemId;
  };

  const unregister8LayerSystem = (systemId) => {
    if (state.depthLayers.has(systemId)) {
      state.depthLayers.delete(systemId);
      log(`8-layer system unregistered: ${systemId}`);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  const NovaMotion = {
    __initialized: true,
    __version: '2.1.0-winter27',
    debug: DEBUG,
    reducedMotion: prefersReduced,

    // Smooth scrolling
    scrollTo(target, opts = {}) {
      if (prefersReduced || !state.lenis) {
        const el = typeof target === 'string' ? document.querySelector(target) : target;
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const {
        offset = 0,
        duration = 1.2,
        immediate = false,
        lock = true
      } = opts;

      state.lenis.scrollTo(target, { offset, duration, immediate, lock });
    },

    // Micro-interactions helper
    animate(el, keyframes, options) {
      if (!el) return null;
      if (prefersReduced) {
        const last = keyframes && keyframes.length ? keyframes[keyframes.length - 1] : null;
        if (last) Object.assign(el.style, last);
        return null;
      }
      if (el.animate) return el.animate(keyframes, options);
      return null;
    },

    // Get Lenis instance (for GSAP ScrollTrigger sync)
    getLenis() {
      return state.lenis;
    },

    // ═══════════════════════════════════════════════════════════════════════
    // WINTER '27: NEW PUBLIC APIS
    // ═══════════════════════════════════════════════════════════════════════

    // Haptic feedback
    haptic(pattern = [10]) {
      return triggerHaptic(pattern);
    },

    // Achievement system
    unlockAchievement(id, data) {
      return unlockAchievement(id, data);
    },

    getAchievements() {
      return Array.from(state.achievements.values());
    },

    hasAchievement(id) {
      return state.achievements.has(id);
    },

    // Variable fonts
    registerVariableFont(element, config) {
      return registerVariableFont(element, config);
    },

    updateVariableFont(fontId, weight) {
      return updateVariableFont(fontId, weight);
    },

    // 8-layer depth systems
    register8LayerSystem(systemId, config) {
      return register8LayerSystem(systemId, config);
    },

    unregister8LayerSystem(systemId) {
      return unregister8LayerSystem(systemId);
    },

    get8LayerSystems() {
      return Array.from(state.depthLayers.keys());
    },

    // Renaissance Journey detection
    setRenaissanceActive(active) {
      state.renaissanceActive = active;
      log(`Renaissance Journey: ${active ? 'ACTIVE' : 'INACTIVE'}`);
    },

    isRenaissanceActive() {
      return state.renaissanceActive;
    },

    // ═══════════════════════════════════════════════════════════════════════
    // MODULE REGISTRY (v2.0 compatible)
    // ═══════════════════════════════════════════════════════════════════════

    registerModule(name, initFn, cleanupFn = null) {
      if (!name || typeof initFn !== 'function') {
        console.warn('⚠️ [NovaMotion] registerModule requires name and initFn');
        return;
      }

      logCritical(`📦 Renai module registered: ${name}`);

      state.modules.set(name, { 
        initFn: () => {
          try {
            const result = initFn(NovaMotion);
            log(`✅ ${name} initialized`);
            return result;
          } catch (e) {
            console.error(`❌ ${name} failed to initialize:`, e);
            return null;
          }
        },
        cleanupFn,
        instance: null 
      });

      // If core is already booted, initialize immediately
      if (state.lenis) {
        const mod = state.modules.get(name);
        if (mod && !mod.instance) {
          mod.instance = mod.initFn();
        }
      }
    },

    // Utility: Get registered module
    getModule(name) {
      const mod = state.modules.get(name);
      return mod ? mod.instance : null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LENIS INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  const initLenis = () => {
    // Wait for Lenis to be available (CDN loads with defer)
    if (typeof Lenis === 'undefined') {
      log('⏳ Waiting for Lenis CDN to load...');
      setTimeout(initLenis, 100); // Retry after 100ms
      return;
    }

    if (prefersReduced) {
      log('⚠️ Reduced motion detected - skipping Lenis');
      return;
    }

    try {
      state.lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
        smoothTouch: false,
        touchMultiplier: 2,
        infinite: false
      });

      // RAF loop
      function raf(time) {
        state.lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);

      // GSAP ScrollTrigger integration
      if (typeof gsap !== 'undefined' && gsap.registerPlugin) {
        state.lenis.on('scroll', () => {
          if (typeof ScrollTrigger !== 'undefined') {
            ScrollTrigger.update();
          }
        });
      }

      logCritical('✅ Lenis smooth scroll initialized');

      // Initialize all registered modules
      state.modules.forEach((mod, name) => {
        if (!mod.instance) {
          mod.instance = mod.initFn();
        }
      });

    } catch (e) {
      console.error('❌ [NovaMotion] Lenis init failed:', e);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // REVEAL-ON-SCROLL (IntersectionObserver)
  // ═══════════════════════════════════════════════════════════════════════════

  const initReveal = () => {
    if (prefersReduced) return;

    const targets = document.querySelectorAll('[data-nova-reveal]');
    if (!targets.length) return;

    state.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !state.revealed.has(entry.target)) {
          entry.target.classList.add('revealed');
          state.revealed.add(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -10% 0px' });

    targets.forEach((el) => state.io.observe(el));
    log(`Reveal-on-scroll watching ${targets.length} elements`);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LIFECYCLE: BOOT
  // ═══════════════════════════════════════════════════════════════════════════

  const boot = () => {
    logCritical(`
┌─────────────────────────────────────────────────┐
│  NOVA MOTION CORE v2.1 - WINTER '27 EDITION     │
│  • 8-layer depth coordination                   │
│  • Variable font animations                     │
│  • Haptic feedback system                       │
│  • Achievement tracking                         │
│  • Renaissance Journey support                  │
└─────────────────────────────────────────────────┘
    `);

    initHaptics();
    loadAchievements();
    initLenis();
    initReveal();

    // Detect Renaissance Journey
    const checkRenaissance = setInterval(() => {
      const renaissanceSection = document.querySelector('[data-renaissance-journey]');
      if (renaissanceSection) {
        NovaMotion.setRenaissanceActive(true);
        clearInterval(checkRenaissance);
      }
    }, 500);

    // Clear check after 5s
    setTimeout(() => clearInterval(checkRenaissance), 5000);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // LIFECYCLE: CLEANUP
  // ═══════════════════════════════════════════════════════════════════════════

  const cleanup = () => {
    if (state.destroying) return;
    state.destroying = true;

    log('Cleaning up NovaMotion...');

    // Cleanup modules
    state.modules.forEach((mod, name) => {
      if (mod.cleanupFn && mod.instance) {
        try {
          mod.cleanupFn(mod.instance);
          log(`Module cleaned up: ${name}`);
        } catch (e) {
          console.error(`Failed to cleanup ${name}:`, e);
        }
      }
    });

    if (state.lenis) {
      state.lenis.destroy();
      state.lenis = null;
    }

    if (state.io) {
      state.io.disconnect();
      state.io = null;
    }

    state.modules.clear();
    state.revealed = new WeakSet();
    state.depthLayers.clear();
    state.variableFonts.clear();
    state.destroying = false;

    log('Cleanup complete');
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // EVENT LISTENERS
  // ═══════════════════════════════════════════════════════════════════════════

  // Shopify Theme Editor section events
  document.addEventListener('shopify:section:load', (e) => {
    log('Section loaded:', e.detail.sectionId);
    initReveal();
  });

  document.addEventListener('shopify:section:unload', (e) => {
    log('Section unloaded:', e.detail.sectionId);
    const cleanup = state.sectionCleanups.get(e.detail.sectionId);
    if (cleanup) {
      cleanup();
      state.sectionCleanups.delete(e.detail.sectionId);
    }
  });

  // BFCache support
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && state.lenis) {
      state.lenis.resize();
      state.lenis.scrollTo(0, { immediate: true });
      log('BFCache restore - Lenis reset');
    }
  });

  window.addEventListener('pagehide', () => {
    if (state.lenis) {
      state.lenis.scrollTo(0, { immediate: true });
    }
  });

  // Cleanup on unload
  window.addEventListener('beforeunload', cleanup);

  // ═══════════════════════════════════════════════════════════════════════════
  // EXPOSE API & BOOT
  // ═══════════════════════════════════════════════════════════════════════════

  window.NovaMotion = NovaMotion;

  // Boot when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();